import type {
  CostLimitsListResponse,
  GetAdminTaskDetailResponse,
  GetAdminUsageResponse,
  GetAdminUserUsageResponse,
  ListAdminTasksResponse,
  ListAdminTaskVersionsResponse,
  ListAdminUsersResponse,
  UserCostLimitDetailResponse,
} from '@kyumo/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteDefaultCostLimit,
  deleteUserCostLimit,
  fetchCostLimits,
  fetchTaskDetail,
  fetchTasks,
  fetchTaskVersion,
  fetchTaskVersions,
  fetchUsageData,
  fetchUserCostLimitDetail,
  fetchUsers,
  fetchUserUsageData,
  setDefaultCostLimit,
  setUserCostLimit,
} from '../services/admin-api';

/**
 * 使用量データを取得するフック
 */
export function useUsageData(month?: string, model?: string) {
  return useQuery<GetAdminUsageResponse>({
    queryKey: ['admin', 'usage', month, model],
    queryFn: () => fetchUsageData(month, model),
  });
}

/**
 * ユーザー別使用量データを取得するフック
 */
export function useUserUsageData(email: string, month?: string, model?: string) {
  return useQuery<GetAdminUserUsageResponse>({
    queryKey: ['admin', 'usage', 'user', email, month, model],
    queryFn: () => fetchUserUsageData(email, month, model),
    enabled: !!email,
  });
}

/**
 * ユーザー一覧を取得するフック（全件取得）
 */
export function useUsers() {
  return useQuery<ListAdminUsersResponse>({
    queryKey: ['admin', 'users'],
    queryFn: () => fetchUsers(),
  });
}

/**
 * タスク一覧を取得するフック（全件）
 */
export function useTasks(email?: string) {
  return useQuery<ListAdminTasksResponse>({
    queryKey: ['admin', 'tasks', email],
    queryFn: () => fetchTasks(email),
  });
}

/**
 * タスク詳細を取得するフック
 */
export function useTaskDetail(email: string, taskId: string) {
  return useQuery<GetAdminTaskDetailResponse>({
    queryKey: ['admin', 'task', email, taskId],
    queryFn: () => fetchTaskDetail(email, taskId),
    enabled: !!email && !!taskId,
  });
}

/**
 * タスクバージョン一覧を取得するフック
 */
export function useTaskVersions(email: string, taskId: string) {
  return useQuery<ListAdminTaskVersionsResponse>({
    queryKey: ['admin', 'task', email, taskId, 'versions'],
    queryFn: () => fetchTaskVersions(email, taskId),
    enabled: !!email && !!taskId,
  });
}

/**
 * 特定バージョンのタスク詳細を取得するフック
 */
export function useTaskVersion(email: string, taskId: string, version: number | undefined) {
  return useQuery<GetAdminTaskDetailResponse>({
    queryKey: ['admin', 'task', email, taskId, 'version', version],
    queryFn: () => fetchTaskVersion(email, taskId, version as number),
    enabled: !!email && !!taskId && version !== undefined,
  });
}

// ==================== コスト上限フック ====================

/**
 * コスト上限一覧を取得するフック
 */
export function useCostLimits() {
  return useQuery<CostLimitsListResponse>({
    queryKey: ['admin', 'cost-limits'],
    queryFn: () => fetchCostLimits(),
  });
}

/**
 * ユーザー個別コスト上限詳細を取得するフック
 */
export function useUserCostLimitDetail(email: string) {
  return useQuery<UserCostLimitDetailResponse>({
    queryKey: ['admin', 'cost-limits', 'user', email],
    queryFn: () => fetchUserCostLimitDetail(email),
    enabled: !!email,
  });
}

/**
 * デフォルトコスト上限を設定するミューテーション
 */
export function useSetDefaultCostLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (limitUSD: number) => setDefaultCostLimit(limitUSD),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cost-limits'] });
    },
  });
}

/**
 * デフォルトコスト上限を削除するミューテーション
 */
export function useDeleteDefaultCostLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteDefaultCostLimit(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cost-limits'] });
    },
  });
}

/**
 * ユーザー個別コスト上限を設定するミューテーション
 */
export function useSetUserCostLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ email, limitUSD }: { email: string; limitUSD: number }) =>
      setUserCostLimit(email, limitUSD),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cost-limits'] });
    },
  });
}

/**
 * ユーザー個別コスト上限を削除するミューテーション
 */
export function useDeleteUserCostLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => deleteUserCostLimit(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cost-limits'] });
    },
  });
}
